
import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import {
  type DateRange,
  type DayButtonProps,
} from "react-day-picker";
import {
  ApplyButton,
  CalendarWrapper,
  CancelButton,
  DatePickerFixedTop,
  DatePickerLayout,
  DayButton,
  DayInner,
  DayNumber,
  FooterActions,
  InputBox,
  InputButton,
  InputClearButton,
  InputGrid,
  InputLabel,
  InputValue,
  RangeEnd,
  RangeMiddle,
  RangeStart,
  StyledDayPicker,
} from "./styles";
import { CalendarDate } from "@solar-icons/react";

export interface FlightDatePickerProps {
  onClose: () => void;
  onApply?: (range: DateRange | undefined) => void;
  initialRange?: DateRange;
  defaultMonth?: Date;
  minDate?: Date;
  singleDateOnly?: boolean;
  isMulti?: boolean;
}

const INPUT_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  day: "numeric",
  month: "short",
  // timeZone: "UTC",
});

const MONTH_CAPTION_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
  // timeZone: "UTC",
});

const FARE_FORMATTER = new Intl.NumberFormat("en-US");

function startOfDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

function getDaysInMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

function getFareForDate(date: Date) {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const seed = (month * 41 + day * 73) % 31;
  return 13500 + seed * 250;
}

function formatInputDate(date?: Date) {
  return date ? INPUT_DATE_FORMATTER.format(date) : "";
}

function PickerInput({
  label,
  value,
  placeholder,
  isActive = false,
  hideLabel = false,
  onClick,
  onClear,
}: {
  label: string;
  value?: string;
  placeholder: string;
  isActive?: boolean;
  hideLabel?: boolean;
  onClick?: () => void;
  onClear?: () => void;
}) {
  const hasValue = Boolean(value);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    event.preventDefault();
    onClick?.();
  };

  return (
    <InputButton
      className="date-input-button"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      {!hideLabel && <InputLabel>{label}</InputLabel>}

      <InputBox
        className={`date-input-box ${isActive ? "active" : ""}`}
        $isActive={isActive}
        $hasValue={hasValue}
      >
        <CalendarDate />
        <InputValue className="date-input-value" $hasValue={hasValue}>
          {value || placeholder}
        </InputValue>

        {hasValue && (
          <InputClearButton
            type="button"
            className="date-clear-button"
            aria-label={`Clear ${label.toLowerCase()} date`}
            onClick={(event) => {
              event.stopPropagation();
              onClear?.();
            }}
          >
            x
          </InputClearButton>
        )}
      </InputBox>
    </InputButton>
  );
}

function FlightDatePicker({
  onClose,
  onApply,
  initialRange,
  defaultMonth,
  minDate,
  singleDateOnly,
  isMulti,
}: FlightDatePickerProps) {
  const today = useMemo(() => startOfDay(minDate ?? new Date()), [minDate]);
  //  const { updateFormData } = useFlightSearchStore();

  const initialMonth = useMemo(
    () => startOfMonth(defaultMonth ?? initialRange?.from ?? today),
    [defaultMonth, initialRange?.from, today]
  );

  const [month, setMonth] = useState(initialMonth);
  const [range, setRange] = useState<DateRange | undefined>(initialRange);
  const [activeField, setActiveField] = useState<"from" | "to">("from");

  const visibleMonthCount = singleDateOnly ? 1 : 2;
  const canApply = Boolean(range?.from || initialRange?.from);

  const lowestVisibleFare = useMemo(() => {
    let minFare = Number.POSITIVE_INFINITY;

    for (let monthOffset = 0; monthOffset < visibleMonthCount; monthOffset += 1) {
      const currentMonth = addMonths(month, monthOffset);
      const daysInMonth = getDaysInMonth(currentMonth);

      for (let dayNumber = 1; dayNumber <= daysInMonth; dayNumber += 1) {
        const day = new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth(),
          dayNumber
        );

        if (day < today) continue;

        minFare = Math.min(minFare, getFareForDate(day));
      }
    }

    return minFare;
  }, [month, today, visibleMonthCount]);

  useEffect(() => {
    setRange(initialRange);
    if (initialRange?.from) {
      setMonth(startOfMonth(initialRange.from));
    }
  }, [initialRange?.from?.getTime(), initialRange?.to?.getTime()]);
  

//   useEffect(() => {
//   if (initialRange?.to) {
//     const tripType = initialRange.to ? "round_trip" : "one_way";
//     updateFormData({
//       tripType,
//     });
//   }
// }, [initialRange?.to?.getTime()]);

  const FareDayButton = ({
    day,
    modifiers,
    className,
    ...buttonProps
  }: DayButtonProps) => {
    const isOutside = Boolean(modifiers.outside);
    const isDisabled = Boolean(modifiers.disabled);
    const isRangeStart = Boolean(modifiers.range_start);
    const isRangeEnd = Boolean(modifiers.range_end);
    const isRangeMiddle = Boolean(modifiers.range_middle);

    const isRangeEdge =
      isRangeStart ||
      isRangeEnd ||
      (Boolean(modifiers.selected) && !range?.to);

    const fare =
      !isOutside && !isDisabled ? getFareForDate(day.date) : undefined;

    const isCheapest = fare === lowestVisibleFare;

    return (
      <DayButton
        {...buttonProps}
        className={className}
        $isDisabled={isDisabled}
        $isOutside={isOutside}
      >
        {isRangeMiddle && <RangeMiddle />}
        {isRangeStart && range?.to && <RangeStart />}
        {isRangeEnd && range?.from && <RangeEnd />}

        <DayInner
          $isRangeEdge={isRangeEdge}
          $isRangeMiddle={isRangeMiddle}
          $isDisabled={isDisabled}
        >
          <DayNumber>{day.date.getDate()}</DayNumber>

          {/* <FareText
            $isOutside={isOutside}
            $isDisabled={isDisabled}
            $isRangeEdge={isRangeEdge}
            $isCheapest={isCheapest}
          >
            {isOutside ? "" : fare ? `KES ${FARE_FORMATTER.format(fare)}` : "-"}
          </FareText> */}
        </DayInner>
      </DayButton>
    );
  };

  const disabledBeforeDate =
    activeField === "to" && range?.from
      ? startOfDay(range.from)
      : today;
  const inputColumnCount = singleDateOnly ? 1 : 2;

  return (
    <DatePickerLayout>
      <DatePickerFixedTop>
      <InputGrid $columnCount={inputColumnCount}>
        <PickerInput
          label="Departure"
          value={formatInputDate(range?.from)}
          placeholder="Select departure date"
          isActive={activeField === "from"}
          onClick={() => setActiveField("from")}
          onClear={() => {
            setRange(undefined);
            setMonth(startOfMonth(new Date()));
            setActiveField("from");
          }}
        />

        {!singleDateOnly && (
          <PickerInput
            label="Return (Optional)"
            value={formatInputDate(range?.to)}
            placeholder="Add a return date"
            isActive={activeField === "to"}
            onClick={() => setActiveField("to")}
            onClear={() => {
              setRange((prev) =>
                prev?.from ? { from: prev.from, to: undefined } : undefined
              );
              setActiveField("to");
            }}
          />
        )}
        </InputGrid>
      </DatePickerFixedTop>

      <CalendarWrapper>
        <StyledDayPicker
          $visibleMonthCount={visibleMonthCount}
          mode="range"
          month={month}
          onMonthChange={setMonth}
          numberOfMonths={visibleMonthCount}
          pagedNavigation
          weekStartsOn={1}
          selected={range}
          onSelect={(_, selectedDay) => {
            if (!selectedDay) return;

            const pickedDay = new Date(
              selectedDay.getFullYear(),
              selectedDay.getMonth(),
              selectedDay.getDate()
            );

            if (singleDateOnly) {
              const nextRange = {
                from: pickedDay,
                to: undefined,
              };

              setRange(nextRange);

              if (isMulti) {
                onApply?.(nextRange);
                onClose();
              }
              return;
            }

            if (activeField === "from") {
              setRange({
                from: pickedDay,
                to: undefined,
              });

              setActiveField("to");
              return;
            }

            setRange((prev) => {
              if (!prev?.from || pickedDay < prev.from) {
                return {
                  from: pickedDay,
                  to: undefined,
                };
              }

              setActiveField("from");

              return {
                from: prev.from,
                to: pickedDay,
              };
            });
          }}
          disabled={{ before: disabledBeforeDate }}
          components={{ DayButton: FareDayButton }}
          formatters={{
            formatCaption: (date) => MONTH_CAPTION_FORMATTER.format(date),
          }}
        />
      </CalendarWrapper>

      <FooterActions>
        <CancelButton type="button" onClick={onClose}>
          Cancel
        </CancelButton>

        <ApplyButton
          type="button"
          onClick={() => {
            onApply?.(range);
            onClose();
          }}
          disabled={!canApply}
        >
          {singleDateOnly ? "Set date" : "Set date(s)"}
        </ApplyButton>
      </FooterActions>
    </DatePickerLayout>
  );
}

export default FlightDatePicker;
